import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Box, Stack, TextField } from "@mui/material";
import { SquareIconButton, useToast } from "@qubit/autoparts";
import { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { mixpanelTrack, useMixpanelPageName } from "~/lib/mixpanel-tracking";
import { QUANTITY_MAX_VALUE } from "~/lib/shared";
import { PutAwayTaskSummaryDto } from "~/types/api";

import {
  selectChangedQuantity,
  selectIsPreviousBinFlow,
  setChangedQuantity
} from "./autostorePutaway.slice";

type Props = {
  selectedRow: PutAwayTaskSummaryDto;
};

export const QuantityField = (props: Props) => {
  const { selectedRow } = props;
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { warningToast } = useToast();
  const trackedPageName = useMixpanelPageName();
  const quantityMaxValue = QUANTITY_MAX_VALUE;
  const remainingValue = selectedRow?.remaining.value ?? quantityMaxValue;

  const changedQuantity = useAppSelector(selectChangedQuantity);
  const isPreviousBinFlow = useAppSelector(selectIsPreviousBinFlow);

  const handleQuantityChangeValue = (value: number) => {
    if (value <= remainingValue && value <= quantityMaxValue) {
      dispatch(setChangedQuantity(value));
    } else {
      const remainingOrMax = Math.min(remainingValue, quantityMaxValue);
      warningToast(
        t("max quantity for selected item is") + ` ${remainingOrMax}`
      );
    }
  };

  const handleQuantityChange = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const inputValue = parseInt(e.target.value || "0", 10);

    handleQuantityChangeValue(inputValue);
  };

  const handleQuantityBlur = () => {
    if (changedQuantity) {
      mixpanelTrack({
        trackedPageName,
        type: "Input Change",
        label: "Quantity",
        eventProperties: {
          Quantity: changedQuantity
        }
      });
    }
  };

  const onClickIncreaseQuantity = () => {
    if (!selectedRow) {
      return;
    }
    const previousQuantity = changedQuantity ?? selectedRow.remaining.value;
    const newQuantity =
      changedQuantity === undefined ? 1 : previousQuantity + 1;

    mixpanelTrack({
      trackedPageName,
      type: "Button Click",
      label: "Increase Quantity",
      eventProperties: {
        Quantity: newQuantity
      }
    });

    handleQuantityChangeValue(newQuantity);
  };

  const onClickDecreaseQuantity = () => {
    if (!selectedRow) {
      return;
    }
    const previousQuantity = changedQuantity ?? selectedRow.remaining.value;
    const newQuantity =
      changedQuantity === undefined ? 0 : previousQuantity - 1;
    if (newQuantity > 0) {
      mixpanelTrack({
        trackedPageName,
        type: "Button Click",
        label: "Decrease Quantity",
        eventProperties: {
          Quantity: newQuantity
        }
      });
      dispatch(setChangedQuantity(newQuantity));
    }
  };

  return (
    <Box sx={{ display: "flex", gap: 1, height: "100%" }}>
      <TextField
        label={t("quantity")}
        variant="outlined"
        inputProps={{
          style: {
            fontSize: "48px",
            textAlign: "center"
          }
        }}
        InputLabelProps={{
          sx: {
            color: "gray.main.dark",
            fontSize: 22
          },
          shrink: true
        }}
        sx={{
          "& .MuiInputBase-root": {
            height: "100%"
          },
          "& .MuiOutlinedInput-notchedOutline > legend": {
            fontSize: "unset"
          }
        }}
        onChange={handleQuantityChange}
        onBlur={handleQuantityBlur}
        value={changedQuantity || ""}
        error={!changedQuantity && !isPreviousBinFlow}
        disabled={isPreviousBinFlow}
      />

      <Stack direction="column" gap={2} justifyContent="center">
        <SquareIconButton
          aria-label="quantity-increase"
          onClick={onClickIncreaseQuantity}
          size="large"
          disabled={isPreviousBinFlow}
        >
          <KeyboardArrowUpIcon />
        </SquareIconButton>
        <SquareIconButton
          aria-label="quantity-decrease"
          onClick={onClickDecreaseQuantity}
          size="large"
          disabled={!changedQuantity || isPreviousBinFlow}
        >
          <KeyboardArrowDownIcon />
        </SquareIconButton>
      </Stack>
    </Box>
  );
};
